import { useMemo } from 'react';

/**
 * The toys that pour in on beat 2 and puff away on beat 3, ported from the
 * design handoff's `buildFallingToys`. Eight glyphs repeated across 250
 * instances, positioned and timed by index so the pattern is deterministic
 * rather than random — it looks the same every play, as the design's does.
 */
const SHAPES: readonly { viewBox: string; paths: readonly string[] }[] = [
  { viewBox: '0 0 24 24', paths: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18', 'M3 12h18', 'M12 3c3 4 3 14 0 18'] },
  { viewBox: '0 0 24 24', paths: ['M12 3l2.4 5.3L20 9l-4 3.8 1 5.7-5-2.9-5 2.9 1-5.7-4-3.8 5.6-.7z'] },
  { viewBox: '0 0 24 24', paths: ['M5 5h14v14H5z', 'M9 9h6v6H9z'] },
  { viewBox: '0 0 24 24', paths: ['M12 2a6 7 0 0 1 0 14 6 7 0 0 1 0-14', 'M12 16v5'] },
  { viewBox: '0 0 24 24', paths: ['M12 21s-7-4.5-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 11c0 5.5-7 10-7 10z'] },
  { viewBox: '0 0 24 24', paths: ['M5 14l2-7h9l4 4h-2', 'M8 17a2 2 0 1 0 0-.1', 'M17 17a2 2 0 1 0 0-.1'] },
  { viewBox: '0 0 24 24', paths: ['M12 2l7 7-7 13-7-13z', 'M5 9h14'] },
  { viewBox: '0 0 24 24', paths: ['M8 6h8v9a4 4 0 0 1-8 0z', 'M9 6c0-2 6-2 6 0'] },
];

const TOY_COUNT = 250;

function ReelToys({ puffing }: { puffing: boolean }) {
  const toys = useMemo(
    () =>
      Array.from({ length: TOY_COUNT }, (_, i) => {
        const shape = SHAPES[i % SHAPES.length];
        return {
          key: i,
          shape,
          left: `${1 + ((i * 37) % 94)}%`,
          bottom: `${2 + ((i * 53) % 22)}px`,
          size: 12 + (i % 5) * 2,
          rotate: ((i * 47) % 44) - 22,
          durationS: (1.5 + (i % 6) * 0.12).toFixed(2),
          delayS: (i * 0.018 + (i % 5) * 0.02).toFixed(2),
        };
      }),
    [],
  );

  return (
    <div aria-hidden='true' className='pointer-events-none absolute inset-0 z-[3] overflow-hidden'>
      {toys.map((toy) => (
        <span
          key={toy.key}
          className={puffing ? 'reel-toy-puff absolute' : 'reel-toy absolute'}
          style={{
            left: toy.left,
            bottom: toy.bottom,
            transform: `rotate(${toy.rotate}deg)`,
            animationDuration: `${toy.durationS}s`,
            animationDelay: `${toy.delayS}s`,
          }}
        >
          <svg
            width={toy.size}
            height={toy.size}
            viewBox={toy.shape.viewBox}
            fill='none'
            stroke='rgba(255,255,255,0.82)'
            strokeWidth='1.6'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            {toy.shape.paths.map((d) => (
              <path key={d} d={d} />
            ))}
          </svg>
        </span>
      ))}
    </div>
  );
}

export default ReelToys;
